import * as React from 'react';
import { formatPretty } from 'services/string';

interface ToolCallProps {
  status: 'complete' | 'inProgress' | 'executing';
  name?: string;
  args?: any;
  result?: any;
}

export default function MCPToolCall({
  status,
  name = '',
  args,
  result,
}: ToolCallProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Format content for display
  const format = (content: any): string => {
    if (!content) return '';
    const text =
      typeof content === 'object'
        ? JSON.stringify(content, null, 2)
        : String(content);
    return text
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  };

  // Support splitting camel case and hyphens in the tool name for readability
  const splitName = (input: string) => {
    if (!input) return '';
    // Replace hyphens with spaces, then split camel case (with a space, preserving abbreviations)
    return input
      .replace(/-/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // before capital letters
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // e.g. 'MCPTool' -> 'MCP Tool'
      .replace(/\s+/g, ' ') // squish multiple spaces
      .trim();
  };
  const formattedName =
  name ? formatPretty(splitName(name)) : 'MCP Tool Call';

  return (
    <div className="w-full overflow-hidden rounded-lg bg-[#1e2738]">
      <div
        className="flex cursor-pointer items-center p-3"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="overflow-hidden text-sm text-ellipsis text-white">
          {formattedName}
        </span>
        <div className="ml-auto">
          <div
            className={`h-2 w-2 rounded-full ${
              status === 'complete'
                ? 'bg-green-400'
                : status === 'inProgress' || status === 'executing'
                  ? 'animate-pulse bg-gray-400'
                  : 'bg-red-400'
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="px-4 pb-4 font-mono text-xs text-gray-300">
          {args && (
            <div className="mb-4">
              <div className="mb-2 text-gray-400">Parameters:</div>
              <pre className="max-h-[200px] overflow-auto whitespace-pre-wrap">
                {format(args)}
              </pre>
            </div>
          )}

          {status === 'complete' && result && (
            <div>
              <div className="mb-2 text-gray-400">Result:</div>
              <pre className="max-h-[200px] overflow-auto whitespace-pre-wrap">
                {format(result)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
